import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCountdown } from 'usehooks-ts';
import { useMediaQueryContext } from '../../../../contexts/MediaQueryContext';
import { Controls } from '../../../../features/AvatarSession/components/Controls';
import { useCompanionChatAnalytics } from '../../../../features/AvatarSession/hooks/useCompanionChatAnalytics';
import { useDataChannel } from '../../../../features/AvatarSession/hooks/useDataChannel';
import { useSessionWebRTC } from '../../../../features/AvatarSession/hooks/useSessionWebRTC';
import { useTwinData } from '../../../../hooks/storeHooks/useTwin';
import { useMicrophones } from '../../../../hooks/useMicrophones';
import { SessionMentor } from '../../../../types/mentor';
import { convertSecondsToTime } from '../../../../utils/timeFormat';
import { Heading } from '../Heading';
import { MediaPermissionsError } from '../MediaPermissionsError';
import { WebCamView } from './WebCamVIew';

const isDev = import.meta.env.MODE === 'development';

interface InterviewTabProps {
  setAudioBlob: (blob: Blob) => void;
  onRestart?: () => void;
}

const MIN_TIME = 540;
const MAX_RECORDING_TIME = 600;
const START_COUNTDOWN = 180;
const MIN_USER_MESSAGES = 5;

const InterviewTab: React.FC<InterviewTabProps> = memo(
  ({ setAudioBlob, onRestart }) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const isMacos = searchParams.get('platform') === 'macos';
    const { isSm } = useMediaQueryContext();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const audioBlobRef = useRef<Blob | null>(null);
    const [avatarReady, setAvatarReady] = useState(false);
    const [needInitCamera] = useState(!isSm);
    const [isFinishButtonForced, setIsFinishButtonForced] = useState(false);
    const [userClosedSession, setUserClosedSession] = useState(false);
    const [validUserMessageCount, setValidUserMessageCount] = useState(0);

    const { sessionId } = useSessionFeatureState();

    const { linkedInSummary, profileJson, setInterviewSessionId } =
      useTwinData();

    const [count, { startCountdown, stopCountdown }] = useCountdown({
      countStart: MAX_RECORDING_TIME,
      intervalMs: 1000,
    });

    const {
      isMuted,
      muteMicrophone,
      stream: userMicrophoneStream,
      selectedMicrophone,
      audioBlob,
      isRecording,
      hasMicrophonePermission,
      connectMicrophone,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
    } = useMicrophones(false);

    const isRecordingRef = useRef(isRecording);

    const { onMessage } = useDataChannel();

    const {
      videoStream,
      audioStream,
      isWebRtcConnected,
      stopWebrtc,
      startWebrtc,
    } = useSessionWebRTC({
      avatarId: '',
      mentorId: '',
      userMicrophoneStream,
      isCapabilitySession: false,
      selectedMicrophone,
      negotiateType: 'interview',
      profileData: {
        linkedInSummary,
        profileJson,
      },
    });

    const handleChangeTalkingState = useCallback(
      (state: string) => {
        if (state === 'canceled' || state === 'ended') {
          if (!isRecordingRef.current) {
            void startVoiceRecording();
            startCountdown();
          } else {
            resumeRecording();
          }
        }

        if (state === 'started' || state === 'in_progress') {
          pauseRecording();
        }
      },
      [startCountdown, resumeRecording, pauseRecording],
    );

    // Set up WebRTC message handler using useDataChannel
    useEffect(() => {
      const unsubscribe = onMessage((data) => {
        switch (data.event) {
          case 'avatar_state_changed':
            if (data.data?.state === 'ready') {
              setAvatarReady(true);
            }
            break;
          case 'talk_state_changed':
            handleChangeTalkingState((data.data?.state as string) || '');
            break;
          case 'message_received': {
            const message = (data.data as { message?: { role?: string; action?: string; content?: string } } | undefined)?.message;
            if (
              message?.role === 'user' &&
              message?.action === 'completed' &&
              typeof message.content === 'string' &&
              message.content.trim().length > 0
            ) {
              setValidUserMessageCount((c) => c + 1);
            }
            break;
          }
          default:
            break;
        }
      });

      return () => {
        unsubscribe();
      };
    }, [onMessage, handleChangeTalkingState]);

    // Analytics: track only companion chat sessions in this interview UI
    useCompanionChatAnalytics({
      enabled: true,
      sessionId: sessionId || null,
      onMessage,
      entryPoint: 'Twin Interview',
      isUserClosed: userClosedSession,
    });

    const startVoiceRecording = async () => {
      if (isRecordingRef.current) return;

      await startRecording();
      startCountdown();
      isRecordingRef.current = true;
    };

    const handleRestartInterview = useCallback(() => {
      // stop current recording and countdown
      stopRecording();
      stopCountdown();

      // reset avatar state
      setAvatarReady(false);

      // stop WebRTC connection
      stopWebrtc();

      // Call parent to trigger remount
      onRestart?.();
    }, [stopRecording, stopCountdown, stopWebrtc, onRestart]);

    const handleStopConnection = useCallback(async () => {
      // Block finish if the user hasn't provided enough valid voice input.
      // The dev shortcut (isFinishButtonForced) still bypasses this for testing.
      if (!isFinishButtonForced && validUserMessageCount < MIN_USER_MESSAGES) {
        toast.error(t('twin.notEnoughVoiceInput'));
        // The Finish button is disabled in this branch, so the only caller is
        // the countdown-timeout effect. Tear down the session and remount the
        // tab — otherwise WebRTC and recording stay live, letting the user
        // keep speaking past the timeout. That produces a mixed audio blob
        // (silence + late speech) and a broken twin voice.
        handleRestartInterview();
        return;
      }

      // Mark that user explicitly finished the interview
      setUserClosedSession(true);

      stopRecording();
      stopCountdown();

      try {
        const blob =
          audioBlob ||
          (await new Promise<Blob>((resolve, reject) => {
            const timeout = setTimeout(
              () => reject(new Error('Timeout waiting for audio blob')),
              5000,
            );
            const checkBlob = () => {
              if (audioBlobRef.current) {
                clearTimeout(timeout);
                resolve(audioBlobRef.current);
              } else {
                setTimeout(checkBlob, 100);
              }
            };
            checkBlob();
          }));

        if (!blob || blob.size === 0) {
          console.error('Invalid audio recording: blob is empty or null');
          throw new Error('Recording failed. Please try again.');
        }
        setAudioBlob(blob);
      } catch (error) {
        console.error('Error processing audio blob:', error);
      } finally {
        stopWebrtc();
      }
    }, [
      audioBlob,
      stopRecording,
      stopCountdown,
      setAudioBlob,
      stopWebrtc,
      isFinishButtonForced,
      validUserMessageCount,
      handleRestartInterview,
      t,
    ]);

    useEffect(() => {
      audioBlobRef.current = audioBlob;
    }, [audioBlob]);

    useEffect(() => {
      if (videoRef.current) videoRef.current.srcObject = videoStream || null;
    }, [videoStream]);

    useEffect(() => {
      if (audioRef.current) audioRef.current.srcObject = audioStream || null;
    }, [audioStream]);

    useEffect(() => {
      if (!isDev) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        console.log('keydown', event);
        // Check for CTRL+CMD+K (Cmd+Ctrl+K on Mac)
        if (event.ctrlKey && event.metaKey && event.key === 'k') {
          event.preventDefault();
          setIsFinishButtonForced(true);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, []);

    useEffect(() => {
      connectMicrophone().catch((error) => {
        console.error('Error connecting microphone:', error);
      });

      return () => {
        stopRecording();
        stopCountdown();
        stopWebrtc();
      };
    }, []);

    useEffect(() => {
      if (count === 0) {
        // Add a buffer time to ensure audio recording finishes processing
        setTimeout(() => {
          handleStopConnection().catch((error) => {
            console.error('Error stopping connection:', error);
          });
        }, 1500); // 1.5 seconds buffer to ensure final audio chunk is captured
      }
    }, [count]);

    useEffect(() => {
      if (sessionId) {
        setInterviewSessionId(sessionId);
      }
    }, [sessionId]);

    useEffect(() => {
      if (!isWebRtcConnected) {
        startWebrtc();
      }
    }, [isWebRtcConnected]);

    const micPermissionWasDeniedRef = useRef(false);
    useEffect(() => {
      if (hasMicrophonePermission === false) {
        micPermissionWasDeniedRef.current = true;
        stopRecording();
        stopCountdown();
        isRecordingRef.current = false;
      } else if (
        hasMicrophonePermission === true &&
        micPermissionWasDeniedRef.current
      ) {
        micPermissionWasDeniedRef.current = false;
        connectMicrophone()
          .then(() => {
            void startVoiceRecording();
          })
          .catch((error) => {
            console.error('Error reconnecting microphone:', error);
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMicrophonePermission]);

    return (
      <div className="w-[750px] max-w-full mx-auto mb-auto sm:mb-0 group relative">
        <Heading
          title={t('twin.interviewTitle')}
          subtitle={t('twin.interviewSubtitle')}
        />
        <div className="relative w-full object-cover aspect-video rounded-2xl tc-mv-blur overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            disablePictureInPicture
            data-testid="avatar-main-video"
            className="w-full h-full rounded-2xl"
          />
          {audioStream && <audio ref={audioRef} autoPlay />}
          {videoStream && (
            <>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4">
                <Controls
                  isMuted={isMuted}
                  audioRef={audioRef}
                  setMuted={muteMicrophone}
                  showScreenShareButton={false}
                  isConnected={!!videoStream}
                  handleConnect={() => {}}
                  handleDisconnect={handleRestartInterview}
                  companion={{} as SessionMentor}
                  disableEndButton
                  disableUploadButton
                  controls
                  isInterview
                />
              </div>
              {needInitCamera && <WebCamView />}
            </>
          )}
          {!avatarReady && (
            <div className="start-experience-btn loader !absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <span className="button-content relative">
                <span className="star-icon star-first">✦</span>
                <span className="star-icon star-second large">✦</span>
                <span className="star-icon star-third">✦</span>
                <span className="text ms-2">{t('twin.connecting')}</span>
              </span>
            </div>
          )}
          {count <= START_COUNTDOWN && (
            <div className="w-24 h-12 flex justify-center items-center rounded-2xl tc-mv-blur text-2xl absolute top-4 right-4">
              {convertSecondsToTime(count)}
            </div>
          )}
        </div>

        <div className="flex gap-4 justify-center mt-8 w-full">
          <button
            data-testid="interview-restart-btn"
            className="btn btn-secondary sm:!grow-0 !grow"
            onClick={handleRestartInterview}
            disabled={!avatarReady}>
            <i className="fa-solid fa-rotate-right mr-2" />
            {t('twin.restart')}
          </button>
          <button
            data-testid="interview-finish-btn"
            className="btn btn-primary sm:!grow-0 !grow"
            onClick={handleStopConnection}
            disabled={
              !isFinishButtonForced &&
              (count > MIN_TIME ||
                !avatarReady ||
                validUserMessageCount < MIN_USER_MESSAGES)
            }>
            {t('twin.finishInterview')}
            <i className="fa-solid fa-arrow-right ml-2" />
          </button>
        </div>

        {hasMicrophonePermission === false && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm rounded-2xl p-6">
            <MediaPermissionsError
              title={t('twin.micRequired')}
              description={t('twin.micDesc')}
              instruction={
                isMacos
                  ? [
                      t('twin.openSystemSettings'),
                      t('twin.enableMicInSettings'),
                      t('twin.restartCompanion'),
                    ]
                  : [
                      t('twin.clickMicIcon'),
                      t('twin.selectAllowMic'),
                      t('twin.refreshPage'),
                    ]
              }
            />
          </div>
        )}
      </div>
    );
  },
);

export { InterviewTab };
